import { useState } from "react";
import { apiRequest } from "../../../shared/lib/api-client.ts";
import type { Project } from "../../leaderboard/types/index.ts";
import { useTrendsDashboardQuery } from "../hooks/use-trends-dashboard-query.ts";
import type { EnhancedMover } from "../types/index.ts";
import { CategoryBarChart, CategoryCards } from "./CategoryBarChart.tsx";
import { CompositionBar } from "./CompositionBar.tsx";
import { MarketTrendChart } from "./MarketTrendChart.tsx";
import { MoversTable } from "./MoversTable.tsx";
import { ScoreDistributionChart } from "./ScoreDistributionChart.tsx";

interface TrendsViewProps {
  onOpenProjectDetail: (project: Project) => void;
}

const PERIODS = [7, 30, 90] as const;

function moverToProject(m: EnhancedMover): Project {
  return {
    id: m.id,
    name: m.name,
    twitter_handle: m.twitterHandle,
    current_alpha_score: m.currentScore,
    confidence_level: null,
    social_points: m.socialPoints,
    backers_points: m.backersPoints,
    pedigree_points: m.pedigreePoints,
    momentum_points: m.momentumPoints,
    dev_points: m.devPoints,
    revenue_points: m.revenuePoints,
    rank_change: null,
    description: m.description ?? null,
    notes: null,
    category_slug: m.categorySlug,
    category_name: m.categoryName,
    category_color: m.categoryColor,
    is_archived: false,
    last_audited_at: m.lastAuditedAt ?? null,
    created_at: "",
    has_tge_hints: m.hasTgeHints,
    tge_confidence: m.tgeConfidence,
    tge_signals: m.tgeSignals,
    tge_sources: m.tgeSources,
  };
}

export function TrendsView({ onOpenProjectDetail }: TrendsViewProps) {
  const [period, setPeriod] = useState<7 | 30 | 90>(7);
  const { data: dashboard, isLoading } = useTrendsDashboardQuery(period, true);

  const handleClickProject = (m: EnhancedMover) => {
    onOpenProjectDetail(moverToProject(m));
  };

  const handleClickHandle = async (handle: string) => {
    if (!dashboard) return;
    const allMovers = [
      ...dashboard.movers.gainers,
      ...dashboard.movers.losers,
      ...dashboard.breakouts,
      ...dashboard.breakdowns,
    ];
    const found = allMovers.find((m) => m.twitterHandle === handle);
    if (found) {
      onOpenProjectDetail(moverToProject(found));
      return;
    }
    // Look up full project data by handle
    try {
      const res = await apiRequest(`/v1/projects?search=${encodeURIComponent(handle)}&limit=5`);
      if (res.ok) {
        const body = (await res.json()) as { data?: Project[] };
        const match = body.data?.find((p) => p.twitter_handle === handle);
        if (match) {
          onOpenProjectDetail(match);
          return;
        }
      }
    } catch {
      /* fall through */
    }
  };

  if (isLoading) {
    return (
      <div className="td-dashboard">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading dashboard</span>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="td-dashboard">
        <div className="empty-state">
          <h3>No trend data available</h3>
          <p>Trend data will appear after scoring runs complete</p>
        </div>
      </div>
    );
  }

  return (
    <div className="td-dashboard">
      {/* 1. Period Selector */}
      <div className="td-period-bar">
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            className={`td-period-pill ${period === p ? "active" : ""}`}
            onClick={() => setPeriod(p)}
          >
            {p}d
          </button>
        ))}
      </div>

      {/* 2. Market Overview Header */}
      <div className="td-overview">
        <div className="td-overview-top">
          <div className={`td-sentiment ${dashboard.sentiment}`}>
            <span className="td-sentiment-icon">
              {dashboard.sentiment === "bullish" && "▲"}
              {dashboard.sentiment === "neutral" && "●"}
              {dashboard.sentiment === "bearish" && "▼"}
            </span>
            <div className="td-sentiment-text">
              <span className="td-label">Market Sentiment</span>
              <span className="td-sentiment-value">{dashboard.sentiment.toUpperCase()}</span>
            </div>
          </div>
          <div className="td-metrics-row">
            <div className="td-metric">
              <span className="td-metric-value">{dashboard.overview.totalProjects}</span>
              <span className="td-label">Scored Projects</span>
            </div>
            <div className="td-metric">
              <span className="td-metric-value">{dashboard.overview.avgScore.toFixed(1)}</span>
              <span className="td-label">Avg Score</span>
            </div>
            <div className="td-metric">
              <span
                className={`td-metric-value ${dashboard.overview.avgScoreDelta > 0 ? "positive" : dashboard.overview.avgScoreDelta < 0 ? "negative" : ""}`}
              >
                {dashboard.overview.avgScoreDelta > 0 ? "+" : ""}
                {dashboard.overview.avgScoreDelta.toFixed(1)}
              </span>
              <span className="td-label">Net Change</span>
            </div>
            <div className="td-metric">
              <span className="td-metric-value positive">{dashboard.overview.breakoutCount}</span>
              <span className="td-label">Breakouts</span>
            </div>
            <div className="td-metric">
              <span className="td-metric-value negative">{dashboard.overview.breakdownCount}</span>
              <span className="td-label">Breakdowns</span>
            </div>
          </div>
        </div>
        <div className="td-overview-charts">
          <ScoreDistributionChart data={dashboard.distribution} />
          <MarketTrendChart data={dashboard.history} period={period} />
        </div>
      </div>

      {/* 3. Top Movers */}
      <div className="td-section">
        <h2 className="td-section-title">Top Movers</h2>
        <div className="td-movers-grid">
          <MoversTable
            title="Gainers"
            movers={dashboard.movers.gainers}
            variant="gainers"
            onClickProject={handleClickProject}
          />
          <MoversTable
            title="Losers"
            movers={dashboard.movers.losers}
            variant="losers"
            onClickProject={handleClickProject}
          />
        </div>
      </div>

      {/* 4. Breakouts & Breakdowns */}
      {(dashboard.breakouts.length > 0 || dashboard.breakdowns.length > 0) && (
        <div className="td-section">
          <h2 className="td-section-title">Breakouts & Breakdowns</h2>
          <div className="td-movers-grid">
            <div className="td-compact-panel">
              <div className="td-panel-header">
                <span className="td-panel-title">Breakouts (+10 pts)</span>
                <span className="td-panel-count">{dashboard.breakouts.length}</span>
              </div>
              {dashboard.breakouts.length === 0 ? (
                <div className="td-empty">None detected</div>
              ) : (
                <div className="td-compact-list">
                  {dashboard.breakouts.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className="td-compact-row td-clickable"
                      onClick={() => handleClickProject(m)}
                    >
                      <span className="td-compact-name">{m.name || `@${m.twitterHandle}`}</span>
                      <span className="td-compact-score">{m.currentScore.toFixed(0)}</span>
                      <span className="td-compact-delta positive">+{m.delta.toFixed(1)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="td-compact-panel">
              <div className="td-panel-header">
                <span className="td-panel-title">Breakdowns (-10 pts)</span>
                <span className="td-panel-count">{dashboard.breakdowns.length}</span>
              </div>
              {dashboard.breakdowns.length === 0 ? (
                <div className="td-empty">None detected</div>
              ) : (
                <div className="td-compact-list">
                  {dashboard.breakdowns.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className="td-compact-row td-clickable"
                      onClick={() => handleClickProject(m)}
                    >
                      <span className="td-compact-name">{m.name || `@${m.twitterHandle}`}</span>
                      <span className="td-compact-score">{m.currentScore.toFixed(0)}</span>
                      <span className="td-compact-delta negative">{m.delta.toFixed(1)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 5. Category Analysis */}
      <div className="td-section">
        <h2 className="td-section-title">Category Analysis</h2>
        <div className="td-cat-section">
          <div className="td-cat-chart-panel">
            <div className="td-panel-header">
              <span className="td-panel-title">Avg Score by Category</span>
            </div>
            <CategoryBarChart categories={dashboard.categories} />
          </div>
          <CategoryCards
            categories={dashboard.categories}
            period={period}
            onClickHandle={handleClickHandle}
          />
        </div>
      </div>

      {/* 6. Score Composition */}
      <div className="td-section">
        <h2 className="td-section-title">Score Composition Overview</h2>
        <div className="td-composition-panel">
          <div className="td-panel-header">
            <span className="td-panel-title">Avg Points per Dimension</span>
          </div>
          <CompositionBar rows={dashboard.composition} />
        </div>
      </div>
    </div>
  );
}
